<template>
  <div id="map">
    <HomeSidebarComponent
      v-if="filterGroupCollection"
      :key="'sidebar-' + mapEpoch"
      ref="sidebar"
      @clientChange="onClientChange"
      @contractTemplateChange="onContractTemplateChange"
      @reset="onResetFilter"
      @filterChange="onFilterChange"
      :filterGroupCollection="filterGroupCollection"
    />

    <DepotList
      ref="depotList"
      :key="'depot-list-' + (last_search_at ? last_search_at.getTime() : '')"
      :depot_selected="depot_selected"
      :models="depots_listed"
      @onItemClick="onItemClick"
      @onBookClick="onBookClick"
    />

    <div class="search">
      <div class="cs-searchbar">
        <i class="cs-search"></i>
        <input
          class="form-control"
          v-clearable
          v-model="state.search"
          @clear="onClear"
          @keyup.prevent.enter="search(state.search, filterGroupCollection)"
          :placeholder="i18n.app.button.search_by_location"
        />
      </div>
    </div>

    <MapComponent
      :models="depots"
      :cluster-epoch="clusterEpoch"
      :myGeometry="myGeometry"
      :selected_filter_depot_ids="selected_filter_depot_ids"
      :markerColors="markerColors"
      @onMarkerPress="onMarkerPressed"
      ref="map"
      :isLoading="!state.ready"
      :showLegend="true"
    />
  </div>
</template>

<script>
import MapComponent, { MapState } from "@/components/map/MapComponent";
import DepotService from "@/services/DepotService";
import GoogleService from "@/services/GoogleService";
import VehicleService from "@/services/VehicleService";
import HomeSidebarComponent from "./partials/HomeSidebarComponent";
import { DepotList } from "@/components/crud/depot";
import FilterGroupCollection from "@/objects/FilterGroupCollection";
import FilterGroup from "@/objects/FilterGroup";
import { mapGetters } from "vuex";
import Client from "@/models/Client";
import _omit from "lodash/omit";
import dialogMixin from "@/mixins/dialog.mixin";
import { Localforage } from "@/boot/localforage/initLocalforage";
import Vue from "vue";
import Depot from "@/models/Depot";

const depotService = new DepotService();
const googleService = new GoogleService();
const MAX_FOCUSED_DEPOTS = 3;

/* Was 30s while a poll cost the server ~2100 queries; both its requests are deltas now. */
const DEPOT_TICKER_INTERVAL_MS = 15 * 1000;
const emptySearch = () => ({ depots: [], syncedAt: null });

export default {
  name: "home",
  mixins: [dialogMixin],
  data() {
    return {
      depots: null,
      filterGroups: FilterGroup.list(this.$route.params.filters),
      filterGroupCollection: null,
      markerColors: this.$route.params.marker_colors,
      depots_focused: null,
      depots_listed: null,
      depot_selected: null,
      myGeometry: null,
      selected_filter_depot_ids: [],
      vehicle_status_ticker: null,
      last_search_at: null,
      latest_ping_at: null,
      refresh_pending: false,
      depotSyncGeneration: 0,
      mapEpoch: 0,
      clusterEpoch: 0,
      sessionBooted: false,
      filtersSnapshot: JSON.parse(
        JSON.stringify(this.$route.params.filters || [])
      ),
      state: {
        search: "",
        ready: false
      }
    };
  },
  watch: {
    sessionReady: {
      immediate: true,
      handler(ready) {
        if (!ready) {
          this.stopDepotTicker();
          return;
        }
        if (this.sessionBooted) return;
        this.sessionBooted = true;
        this.bootFromSession();
      }
    }
  },
  destroyed() {
    this.stopDepotTicker();
  },
  methods: {
    bootFromSession() {
      this.initFromSession().then(depots => {
        this.depots = depots;
        this.localSearch(this.filterGroupCollection, depots);

        this.startDepotTicker();
        this.search(this.state.search, this.filterGroupCollection).then(() => {
          MapState.clear();
        });
      });
    },
    initFromSession() {
      const filterGroupCollectionSession = FilterGroupCollection.load();

      if (filterGroupCollectionSession && filterGroupCollectionSession.client) {
        return this.init(
          filterGroupCollectionSession.contract_template,
          filterGroupCollectionSession.client
        );
      } else {
        return this.init(this.contractTemplate, this.client);
      }
    },
    init(ct, client, forceRefresh = false) {
      const filterGroupCollection = new FilterGroupCollection({
        groups: this.filterGroups,
        contract_template: ct,
        client: client
      });

      const filterGroupCollectionSession = FilterGroupCollection.load();

      if (filterGroupCollectionSession)
        filterGroupCollection.copyStateFrom(filterGroupCollectionSession);

      this.filterGroupCollection = filterGroupCollection;

      return new Promise((resolve, reject) => {
        Localforage.getDepots(filterGroupCollection.save_key).then(depots => {
          if (
            !forceRefresh &&
            depots?.length &&
            !ct?.is_goplanner_profile
          )
            return resolve(depots);
          else {
            return this.initialSearch(filterGroupCollection)
              .then(({ depots }) => resolve(depots))
              .catch(e => {
                console.error("unable to load depots", e);
                resolve([]);
              });
          }
        });
      });
    },
    /** Resolves { depots, syncedAt }; a timestamp narrows it to changes since then. */
    initialSearch(filterGroupCollection, timestamp = null, select = null) {
      if (!this.sessionReady) return Promise.resolve(emptySearch());

      const key = filterGroupCollection.save_key;

      return depotService
        .searchSince({
          client_id: filterGroupCollection.client.id,
          contract_template_id: filterGroupCollection.getContractTemplateId(),
          is_initial_search: true,
          only_changes_from: timestamp,
          select: select,
          selected_vehicle_segment_ids: filterGroupCollection.findFilterValueByFilterKey(
            "vehicle_segment_id"
          )
        })
        .then(result => {
          /* Persisting a projection would leave the cache holding id-only rows. */
          if (timestamp || select) return result;

          return Localforage.saveDepots(
            result.depots,
            key,
            result.syncedAt
          ).then(() => result);
        })
        .catch(e => {
          /* Losing the session is the one failure the caller already handles. */
          if (e?.response?.status === 401) return emptySearch();

          throw e;
        });
    },
    onClear() {
      this.state.search = null;
      this.depots_listed = [];
      this.depots_focused = [];
      this.search(this.state.search, this.filterGroupCollection);
    },
    async search(search, filterGroupCollection, updateMapPosition = true) {
      if (!this.sessionReady) return Promise.resolve();

      this.state.ready = false;
      let geometry = null;
      try {
        geometry = search ? await googleService.findByCity(search) : null;
      } catch (e) {
        this.state.ready = true;
        this.showSimpleMessage(i18n.messages.no_search_results);
        return;
      }

      this.myGeometry = geometry;
      filterGroupCollection.save();

      if (this.shouldDoLocalSearch(filterGroupCollection) && !geometry) {
        this.selected_filter_depot_ids =
          filterGroupCollection.findFilterValueByFilterKey("depot_id") || [];
        this.depots_focused = this.depots.filter(
          item => this.selected_filter_depot_ids.indexOf(item.id) > -1
        );

        this.updateDepotStates(this.depots, this.depots);

        this.afterSearch(
          this.depots_focused?.length ? this.depots_focused : this.depots,
          updateMapPosition
        );
      } else {
        this.last_search_at = new Date();

        return new Promise((resolve, reject) => {
          return depotService
            .search(search, {
              client_id: filterGroupCollection.getClientId(),
              coordinate:
                geometry && geometry?.location
                  ? {
                      latitude: geometry.location.lat,
                      longitude: geometry.location.lng
                    }
                  : null,
              contract_template_id: filterGroupCollection.getContractTemplateId(),
              selected_vehicle_segment_ids: filterGroupCollection.findFilterValueByFilterKey(
                "vehicle_segment_id"
              ),
              meta: {
                filter: filterGroupCollection.getValue(),
                limit: search ? 3 : 0
              }
            })
            .then(items => {
              this.resetSelection();
              this.selected_filter_depot_ids =
                filterGroupCollection.findFilterValueByFilterKey("depot_id") ||
                [];

              const depotIds = items.map(depot => depot.id);

              this.depots_focused = this.selected_filter_depot_ids.length
                ? items.filter(
                    item => this.selected_filter_depot_ids.indexOf(item.id) > -1
                  )
                : search
                ? items
                    // .filter(item => item.vehicles.length > 0)
                    .slice(0, MAX_FOCUSED_DEPOTS)
                : [];

              this.updateDepotStates(this.depots, items, !search);

              this.depots_listed = depotIds
                .map(id => this.depots.find(depot => depot.id == id))
                .filter(depot => depot);

              this.afterSearch(items, updateMapPosition);

              return resolve(items);
            })
            .catch(e => {
              if (e?.status === 401) return;
              console.log("unable to search", e);
              console.log(e);
            });
        });
      }

      return Promise.resolve();
    },
    shouldDoLocalSearch(filterGroupCollection) {
      const filters = filterGroupCollection.getFiltersWithValues();
      if (filterGroupCollection.getContractTemplateId()) {
        return false;
      }
      const selectedDepotsIds =
        filterGroupCollection.findFilterValueByFilterKey("depot_id") || [];
      return (
        this.depots &&
        (filters.length == 0 ||
          (filters.length == 1 && selectedDepotsIds.length > 0))
      );
    },
    localSearch(filterGroupCollection, items) {
      this.resetSelection();
      this.selected_filter_depot_ids =
        filterGroupCollection.findFilterValueByFilterKey("depot_id") || [];
      this.updateDepotStates(this.depots, items);

      this.afterSearch(items);
    },
    afterSearch(items, updateMapPosition = true) {
      this.$refs.depotList?.$forceUpdate();

      this.$nextTick(() => {
        const mapRef = this.$refs.map;

        if (mapRef) {
          mapRef.refreshClusters();

          const mapState = MapState.load();
          const sw = mapState?.bounds?._southWest;
          const ne = mapState?.bounds?._northEast;
          const boundsAreFinite =
            Depot.isFiniteLatLng(sw?.lat, sw?.lng) &&
            Depot.isFiniteLatLng(ne?.lat, ne?.lng);
          const restoreView =
            boundsAreFinite &&
            (mapState.depot_id_selected || mapState.depot_ids_focused?.length);

          if (restoreView) {
            this.depots_focused = this.depots.filter(
              item => (mapState.depot_ids_focused || []).indexOf(item.id) > -1
            );

            this.depot_selected = this.depots.find(
              item => item.id == mapState.depot_id_selected
            );

            if (updateMapPosition) {
              if (this.myGeometry) {
                mapRef.panTo(this.myGeometry.location);
              } else {
                this.applyLatLngs([
                  [sw.lat, sw.lng],
                  [ne.lat, ne.lng]
                ]);
              }
            }
          } else if (updateMapPosition) {
            const latlngs = this.myGeometry ? this.depots_focused : items;

            this.applyLatLngs(this.getMarkerLatLngs(latlngs));
          }
        }

        this.state.ready = true;

        if (this.refresh_pending) {
          this.refresh_pending = false;
          this.$nextTick(() => this.onFilterChange(null));
        }
      });
    },
    startDepotTicker() {
      if (this.depots) this.pingDepots();

      this.depot_ticker = setInterval(() => {
        if (this.depots) this.pingDepots();
      }, DEPOT_TICKER_INTERVAL_MS);
    },
    stopDepotTicker() {
      if (this.depot_ticker) {
        clearInterval(this.depot_ticker);
      }
    },
    pingDepots() {
      if (!this.sessionReady) return Promise.resolve();

      const fgc = this.filterGroupCollection;
      const key = fgc.save_key;
      const generation = this.depotSyncGeneration;
      let depots = this.depots;

      return new Promise(async (resolve, reject) => {
        try {
          const savedTimestamp = await Localforage.getTs(key);
          const timestamp = this.latest_ping_at ? this.latest_ping_at : savedTimestamp;
          const {
            depots: depotsWithChanges,
            syncedAt
          } = await this.initialSearch(fgc, timestamp);
          let hasUpdate = false;

          if (generation !== this.depotSyncGeneration || !this.sessionReady)
            return resolve();

          if (depotsWithChanges?.length) {
            depotsWithChanges.forEach(d => {
              const depot = depots.find(depot => depot.id == d.id);
              if (depot) {
                depot.initializeAttributes(_omit(d, ["vehicles"]));
                depot.setVehicles(d.vehicles);
              }
            });

            hasUpdate = true;
          }

          const allDepotIds = (
            (await this.initialSearch(fgc, 1, ["id"])).depots || []
          ).map(item => item.id);

          if (generation !== this.depotSyncGeneration || !this.sessionReady)
            return resolve();

          const localIds = depots.map(item => item.id);
          const missingDepotIds = allDepotIds.filter(
            id => localIds.indexOf(id) == -1
          );
          const deletedDepotIds = localIds.filter(
            id => allDepotIds.indexOf(id) == -1
          );

          if (missingDepotIds.length) {
            const { depots: fresh } = await this.initialSearch(fgc);
            if (generation !== this.depotSyncGeneration || !this.sessionReady)
              return resolve();
            if (fresh?.length) {
              fresh.forEach(d => {
                if (!d.state) d.initState();
              });
              depots = fresh;
              hasUpdate = true;
            }
          }

          if (deletedDepotIds.length) {
            hasUpdate = true;
            depots = depots.filter(
              item => deletedDepotIds.indexOf(item.id) == -1
            );
          }

          if (generation !== this.depotSyncGeneration || !this.sessionReady)
            return resolve();

          /* Our own clock here is what used to skip changes until the next login. */
          if (syncedAt) this.latest_ping_at = syncedAt;

          if (hasUpdate) {
            this.depots = depots;
            await Localforage.saveDepots(depots, key, this.latest_ping_at);
            this.applyPingedChanges();
          } else if (syncedAt) {
            await Localforage.setTs(syncedAt, key);
          }

          resolve();
        } catch (e) {
          console.error("unable to ping depots", e);
          resolve();
        }
      });
    },
    /** Re-runs the active search, or queues it when one is already in flight. */
    applyPingedChanges() {
      if (!this.state.ready) {
        this.refresh_pending = true;
        return;
      }

      this.onFilterChange(null);
    },
    refreshView() {
      this.updateDepotStates(
        this.depots,
        this.depots.filter(depot => depot.vehicles.length > 0)
      );
      this.$forceUpdate();
      this.$refs.map.$forceUpdate();

      setTimeout(() => {
        /* Fuck this... gonna check later why setTimeout is needed */
        if (this.$refs.map) {
          this.$refs.map.refreshClusters();
        }
      });
    },
    touchDepots() {
      this.depots.forEach(depot => {
        this.touchVehicles(depot.vehicles);
      });

      if (this.depot_selected) this.touchVehicles(this.depot_selected.vehicles);
    },
    touchVehicles(vehicles) {
      vehicles.forEach(vehicle => {
        if (!vehicle.is_available && !vehicle.available_from) {
          const index = vehicles.findIndex(item => item == vehicle);
          vehicles.splice(index, 1);
        }
      });
    },
    onResetFilter() {
      FilterGroupCollection.clear();
      this.filterGroupCollection.reset();
      this.search(this.state.search, this.filterGroupCollection);
    },
    resetSelection() {
      if (this.depot_selected) {
        this.depot_selected.state.focused = false;
        this.depot_selected = null;
      }
      this.selected_filter_depot_ids = [];
    },
    onFilterChange(filter) {
      if (filter?.filter_child) {
        this.filterGroupCollection.applyChildFilters();
        this.$forceUpdate();
      }

      if (!this.state.ready) return;

      this.search(this.state.search, this.filterGroupCollection, !!filter);
    },
    onClientChange(client) {
      this.reset(null, client);
    },
    onContractTemplateChange(ct) {
      const client = new Client(
        ct
          ? this.clients.find(item => item.id == ct.client_id)
          : this.filterGroupCollection.client
      );

      this.reset(ct, client);
    },
    reset(ct, client) {
      this.depotSyncGeneration += 1;
      const generation = this.depotSyncGeneration;
      this.mapEpoch += 1;
      this.latest_ping_at = null;
      this.state.ready = false;
      this.resetSelection();
      FilterGroupCollection.clear();
      this.stopDepotTicker();

      this.filterGroups = FilterGroup.list(this.filtersSnapshot);
      this.depots_listed = [];
      this.depots_focused = [];

      this.init(ct, client, true).then(depots => {
        if (generation !== this.depotSyncGeneration) return;

        this.$refs.map && this.$refs.map.dropClusterFromMap();
        this.clusterEpoch += 1;
        this.depots = (depots || []).slice();
        this.localSearch(this.filterGroupCollection, this.depots);

        this.startDepotTicker();
        this.search(this.state.search, this.filterGroupCollection);
      });
    },
    getVehicles() {
      let vehicles = [];
      this.depots.forEach(depot => {
        vehicles = vehicles.concat(depot.vehicles);
      });

      return vehicles;
    },
    getMarkerLatLngs(items) {
      let fitBoundsItems = items;

      if (
        this.selected_filter_depot_ids &&
        this.selected_filter_depot_ids.length &&
        Array.isArray(items)
      ) {
        fitBoundsItems = items.filter(
          item => this.selected_filter_depot_ids.indexOf(item.id) > -1
        );
      }

      if (!Array.isArray(fitBoundsItems)) {
        return [];
      }

      return fitBoundsItems
        .filter(item => Depot.hasCoordinates(item))
        .map(item => [
          Number(item.address.latitude),
          Number(item.address.longitude)
        ]);
    },
    applyLatLngs(latlngs) {
      const points = (latlngs || []).filter(pair =>
        Depot.isFiniteLatLng(pair?.[0], pair?.[1])
      );

      if (points.length > 1) {
        this.$refs.map.fitBounds(points);
      } else if (points.length == 1) {
        this.$refs.map.panTo({
          lat: points[0][0],
          lng: points[0][1]
        });
      }
    },
    onMarkerPressed(depot) {
      if (
        this.depot_selected &&
        this.selected_filter_depot_ids.indexOf(this.depot_selected.id) == -1
      ) {
        this.depot_selected.state.selected = false;
      }

      this.depot_selected = depot;
      this.depot_selected.state.selected = true;
    },
    onItemClick(depot) {
      this.$refs.map.onMarkerPress(depot);
    },
    onBookClick({ vehicle, depot }) {
      MapState.save(
        this.$refs.map.getBounds(),
        this.depot_selected?.id,
        this.depots_focused ? this.depots_focused.map(item => item.id) : []
      );

      this.$router.push({
        name: "contract-create",
        query: {
          client_id: this.filterGroupCollection.getClientId(),
          vehicle_id: vehicle.id,
          depot_id: depot.id,
          contract_template_id: this.filterGroupCollection.getContractTemplateId()
        }
      });
    },
    updateDepotStates(
      depotsAll,
      depotsWithVehicles = [],
      fallbackToZero = true
    ) {
      if (!depotsWithVehicles) depotsWithVehicles = [];
      const ids = depotsWithVehicles.map(item => item.id);

      depotsAll.forEach(depot => {
        const selected = this.selected_filter_depot_ids.indexOf(depot.id) > -1;

        depot.initState({
          available_vehicles:
            ids.indexOf(depot.id) == -1 && fallbackToZero
              ? []
              : depot.state?.available_vehicles,
          selected: selected,
          focused: selected
        });
      });

      depotsWithVehicles.forEach(item => {
        const depot = depotsAll.find(depot => depot.id == item.id);

        if (depot) {
          if (depot.address && item.address)
            depot.state.distance = item.address.distance;

          depot.setAvailableVehicles(item.vehicles);

          if (!this.selected_filter_depot_ids.length) {
            depot.state.focused =
              depot.state.available_vehicles.length > 0 || this.myGeometry;
          }
        }
      });
    }
  },
  computed: {
    ...mapGetters({
      contractTemplate: "contractTemplate",
      client: "client",
      clients: "clients",
      sessionReady: "sessionReady"
    })
  },
  components: {
    HomeSidebarComponent,
    DepotList,
    MapComponent
  }
};
</script>
